import { useState } from "react";
import { motion } from "framer-motion";
import { Users, Heart, HandHeart, Gift, Loader2, CheckCircle, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    overline: "Planned Initiative",
    title: "The Pillar Project Community Resource Fair",
    subtitle: "A single-day event bringing survivor-service organizations, healthcare, housing, workforce, legal, law enforcement, and community businesses together under one roof — so survivors and community members can connect with vetted providers in one place.",
    note: "Date, venue, and confirmed participating organizations will be announced as arrangements are finalized.",
    whoTitle: "Who We Want In the Room",
    whoText: "We're building the resource fair to represent the full network of organizations survivors need to reach — with knowledgeable people ready to talk in person.",
    orgTypes: ["Survivor-service organizations","Housing resources","Workforce-development providers","Health and wellness organizations","HIV and health-screening providers","Law enforcement","Fire and emergency personnel","Legal-resource organizations","Mental-health providers","Employers","Training organizations","Community businesses"],
    waysTitle: "Ways to Participate",
    interests: [
      { value: "resource_partner", label: "Become a resource partner" },
      { value: "sponsor", label: "Sponsor the event" },
      { value: "volunteer", label: "Volunteer at the fair" },
      { value: "attendee", label: "Register as an attendee" },
      { value: "in_kind", label: "Provide an in-kind donation" },
    ],
    formTitle: "Express Interest",
    formSubtitle: "Tell us how you'd like to engage and we'll follow up as the event takes shape.",
    submittedTitle: "Received",
    submittedText: "Thank you — we'll be in touch as arrangements are confirmed.",
    labels: {
      organization: "Organization", contact_name: "Contact Name", email: "Email", phone: "Phone",
      interest: "How would you like to participate?", select: "Select…", notes: "Notes",
      submit: "Submit Interest", submitting: "Submitting…",
    },
    toastSelect: "Please select how you'd like to participate.",
    toastSuccess: "Thank you — your interest has been recorded.",
    toastError: "Submission failed",
  },
  es: {
    overline: "Iniciativa Planificada",
    title: "Feria de Recursos Comunitarios de The Pillar Project",
    subtitle: "Un evento de un día que reúne a organizaciones de servicios para sobrevivientes, salud, vivienda, empleo, legal, fuerzas del orden y comercios comunitarios bajo un mismo techo — para que los sobrevivientes y miembros de la comunidad puedan conectarse con proveedores verificados en un solo lugar.",
    note: "La fecha, lugar y organizaciones participantes confirmadas se anunciarán cuando los preparativos se finalicen.",
    whoTitle: "Quiénes Queremos en la Sala",
    whoText: "Estamos construyendo la feria de recursos para representar toda la red de organizaciones que los sobrevivientes necesitan alcanzar — con personas conocedoras listas para hablar en persona.",
    orgTypes: ["Organizaciones de servicios para sobrevivientes","Recursos de vivienda","Proveedores de desarrollo laboral","Organizaciones de salud y bienestar","Proveedores de detección de VIH y salud","Fuerzas del orden","Personal de bomberos y emergencia","Organizaciones de recursos legales","Proveedores de salud mental","Empleadores","Organizaciones de capacitación","Comercios comunitarios"],
    waysTitle: "Formas de Participar",
    interests: [
      { value: "resource_partner", label: "Ser socio de recursos" },
      { value: "sponsor", label: "Patrocinar el evento" },
      { value: "volunteer", label: "Ser voluntario en la feria" },
      { value: "attendee", label: "Registrarse como asistente" },
      { value: "in_kind", label: "Ofrecer una donación en especie" },
    ],
    formTitle: "Expresar Interés",
    formSubtitle: "Cuéntanos cómo te gustaría participar y daremos seguimiento a medida que el evento tome forma.",
    submittedTitle: "Recibido",
    submittedText: "Gracias — nos pondremos en contacto cuando los preparativos estén confirmados.",
    labels: {
      organization: "Organización", contact_name: "Nombre de Contacto", email: "Correo", phone: "Teléfono",
      interest: "¿Cómo te gustaría participar?", select: "Seleccionar…", notes: "Notas",
      submit: "Enviar Interés", submitting: "Enviando…",
    },
    toastSelect: "Por favor selecciona cómo te gustaría participar.",
    toastSuccess: "Gracias — tu interés ha sido registrado.",
    toastError: "El envío falló",
  },
};

const INTEREST_ICONS = [HandHeart, Heart, Users, MapPin, Gift];

export default function CommunityFairPage() {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;

  const [form, setForm] = useState({ organization: "", contact_name: "", email: "", phone: "", interest_type: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.interest_type) return toast.error(c.toastSelect);
    setSubmitting(true);
    try {
      await axios.post(`${API}/community-fair/interest`, form);
      toast.success(c.toastSuccess);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || c.toastError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-warm-800 text-white py-16 md:py-24" data-testid="fair-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-300 mb-4 block">{c.overline}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }} data-testid="fair-title">{c.title}</h1>
            <p className="text-lg text-warm-200 leading-relaxed max-w-2xl">{c.subtitle}</p>
            <p className="text-sm text-warm-300 mt-4 italic">{c.note}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-3xl font-medium text-gray-900 mb-4" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.whoTitle}</h2>
              <p className="text-gray-600 mb-6">{c.whoText}</p>
              <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700">
                {c.orgTypes.map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" />{t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-warm-50 border border-warm-100 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.waysTitle}</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                {c.interests.map((o, i) => {
                  const Icon = INTEREST_ICONS[i];
                  return (
                    <li key={o.value} className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-warm-700 flex-shrink-0 mt-0.5" />
                      <span>{o.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-warm-50 border-y border-warm-100" id="interest">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-medium text-gray-900 mb-2" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.formTitle}</h2>
          <p className="text-gray-600 mb-6 text-sm">{c.formSubtitle}</p>

          {submitted ? (
            <div className="bg-white border border-warm-100 rounded-xl p-8 text-center" data-testid="fair-submitted">
              <CheckCircle className="w-12 h-12 text-warm-700 mx-auto mb-3" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.submittedTitle}</h3>
              <p className="text-gray-600">{c.submittedText}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-warm-100 rounded-xl p-6 space-y-4" data-testid="fair-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <F label={c.labels.organization} value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testId="fair-org" />
                <F label={c.labels.contact_name} required value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testId="fair-name" />
                <F label={c.labels.email} type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="fair-email" />
                <F label={c.labels.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="fair-phone" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">{c.labels.interest}<span className="text-red-500 ml-0.5">*</span></label>
                <select required className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" value={form.interest_type} onChange={(e) => setForm({ ...form, interest_type: e.target.value })} data-testid="fair-interest">
                  <option value="">{c.labels.select}</option>
                  {c.interests.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">{c.labels.notes}</label>
                <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="fair-notes" />
              </div>
              <Button type="submit" disabled={submitting} className="bg-warm-700 hover:bg-warm-800 text-white rounded-full px-8 py-5" data-testid="fair-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {c.labels.submitting}</> : c.labels.submit}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function F({ label, value, onChange, required, type = "text", testId }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" data-testid={testId} />
    </div>
  );
}
